import React from 'react'
import debounce from 'lodash.debounce'
import {
  ALLOW_FILE_TYPES,
  ALLOW_SIZE,
  UNAUTHORIZED,
  ALERT_MESSAGES,
  LOCALSTORAGE_ITEM,
  DONT_CHANGE_DOMICILE_FISCALE,
  DOMICILE_FISCALE_DIFFERENT,
  IS_YOUR_DOMICILE_FISCALE_DIFFERENT,
  DEFAULT_ERROR_MESSAGE,
  INTERNAL_SERVER_ERROR,
  DOCUMENT_VERIFICATION_STATUS,
  DOCUMENT_UPLOAD_RETRY_LIMIT,
  DOCUMENT_STATUS_FOR_MODAL_TO_OPEN,
  DOCUMENT_INVALID_STATUS,
} from '../enums/common'
import { EXCLUDED_FRENCH_COLONIES, FRENCH_COLONIES } from '../enums/Countries'
import { IBreadCrumb } from '../interfaces/Breadcrumb'
import { IFileUploadAlert, IUploadDocumentInput } from '../interfaces/Upload'
import { IOption } from '../interfaces/FormFields'
import { validateStep4FranceNumber } from './validation'
import { ROUTES } from '../enums/routes'
import { ICommuneData } from '../interfaces/Address'

export function isUnauthorized(error: Error) {
  const { message } = error
  const status = parseInt(
    message.substring(message.length - 3, message.length),
    10
  )
  return status === UNAUTHORIZED
}

export function isLoggedIn() {
  return localStorage.getItem(LOCALSTORAGE_ITEM) !== null
}

export function activeStep(
  breadcrumbs: IBreadCrumb[],
  journeyStatus: string
): number {
  const getStepFunction = isJourneyRevisit(journeyStatus)
    ? getRevisitStep
    : getActiveStep
  const defaultStep = isJourneyRevisit(journeyStatus) ? 99 : 0
  const activeIdx = breadcrumbs.findIndex(getStepFunction)

  return activeIdx !== -1 ? activeIdx : defaultStep
}

export const isJourneyRevisit = (journeyStatus: string) => journeyStatus === '5'

const getActiveStep = (item: IBreadCrumb) =>
  (item.statut === '' && !item.done) || item.active

const getRevisitStep = (item: IBreadCrumb) =>
  item.revisit === 'true' && !item.done

export const getRevisitSteps = (breadcrumb: IBreadCrumb[]) =>
  breadcrumb.filter((item) => item.revisit === 'true' && !item.done)

export function getFileErrorMessage(
  file: File,
  isMultifile?: boolean
): IFileUploadAlert | null {
  let message = null
  if (!ALLOW_FILE_TYPES.includes(file.type)) {
    message = ALERT_MESSAGES.fileType
  }
  if (file.size > (isMultifile ? ALLOW_SIZE / 2 : ALLOW_SIZE)) {
    message = isMultifile
      ? ALERT_MESSAGES.fileSizeHalf
      : ALERT_MESSAGES.fileSize
  }

  return message
}

export function generateUploadDocumentsInput(options: IUploadDocumentInput) {
  const data = generateUploadDocumentInput(options)
  options.files.forEach((file: File) => data.append('files[]', file))

  return data
}

export function generateUploadDocumentInput(options: IUploadDocumentInput) {
  const data = new FormData()
  data.append('typeId', options.typeId)
  data.append('personTypeId', options.personTypeId)
  data.append('currentStep', options.currentStep)
  if (!Array.isArray(options.files)) {
    data.append('files', options.files)
  }

  return data
}

export function getDocumentStatusAlert(
  status: string,
  documentTypeId: number,
  statusVerification: number
): IFileUploadAlert {
  switch (status) {
    case 'pending':
      return ALERT_MESSAGES.pending
    case 'valid':
      return computeValidMessage(documentTypeId)
    case 'invalid':
      return {
        type: 'error',
        message: `MOTIF DE REFUS DU DOCUMENT PRÉCÉDEMMENT TRANSMIS :`,
        messageLine2: DOCUMENT_INVALID_STATUS[statusVerification],
      }
    default:
      return ALERT_MESSAGES.invalid
  }
}

export function getDocumentWithRetryStatusAlert(
  status: string,
  documentTypeId: number,
  statusVerification: number,
  uploadCount: number
): IFileUploadAlert {
  switch (status) {
    case 'pending':
      if (
        DOCUMENT_STATUS_FOR_MODAL_TO_OPEN.indexOf(statusVerification) !== -1
      ) {
        return ALERT_MESSAGES.empty
      }

      if (uploadCount >= DOCUMENT_UPLOAD_RETRY_LIMIT) {
        return ALERT_MESSAGES.pending
      }
      switch (statusVerification) {
        case DOCUMENT_VERIFICATION_STATUS.NOT_VERIFIED:
        case DOCUMENT_VERIFICATION_STATUS.OUT_OF_BOUNDS:
        case DOCUMENT_VERIFICATION_STATUS.UNREADABLE:
          return ALERT_MESSAGES.pendingNotValid
        case DOCUMENT_VERIFICATION_STATUS.MISSING_VERSO:
          return ALERT_MESSAGES.pendingIncomplete
        case DOCUMENT_VERIFICATION_STATUS.EXPIRED:
          return ALERT_MESSAGES.pendingExpired
        default:
          return ALERT_MESSAGES.pending
      }
    case 'valid':
      return computeValidMessage(documentTypeId)
    case 'invalid':
      return {
        type: 'error',
        message: `MOTIF DE REFUS DU DOCUMENT PRÉCÉDEMMENT TRANSMIS :`,
        messageLine2: DOCUMENT_INVALID_STATUS[statusVerification],
      }
    default:
      return ALERT_MESSAGES.invalid
  }
}

const computeValidMessage = (documentTypeId: number) => {
  return (
    ALERT_MESSAGES.valid[documentTypeId] || {
      type: 'success',
      message: 'Document validé(e)',
    }
  )
}

export const getOptionLabelByValue = (
  options: IOption[],
  value: string
): string => {
  const option = options.find((item) => item.value === value)
  return option ? option.label : ''
}

export const convertDateFromDatePicker = (date: string) => {
  const dateObj = new Date(date)
  // @ts-ignore
  if (isNaN(dateObj)) {
    throw new Error('Invalid Date Format')
  }
  return [
    dateObj.getDate().toString().padStart(2, '0'),
    (dateObj.getMonth() + 1).toString().padStart(2, '0'),
    dateObj.getFullYear(),
  ].join('/')
}

export const triggerChangeForSelectInput = (
  name: string,
  value: number | string
) => {
  const element = document.getElementById(name)
  if (!element) {
    return
  }
  // @ts-ignore
  const trigger = Object.getOwnPropertyDescriptor(
    window.HTMLSelectElement.prototype,
    'value'
  ).set
  // @ts-ignore
  trigger.call(element, value)

  const event = document.createEvent('Event')
  event.initEvent('change', true, true)
  // @ts-ignore
  element.dispatchEvent(event)
}

export const preventEnterFormSubmit = (event: React.KeyboardEvent) => {
  event.persist()
  const { target } = event

  if (event.key === 'Enter' && !isSubmitButton(target as HTMLButtonElement)) {
    event.preventDefault()
  }
}

function isSubmitButton(element: HTMLButtonElement) {
  return element.type === 'submit' && element.nodeName === 'BUTTON'
}

export const getCountriesValues = (countries: IOption[]) => {
  return countries.map((country) => country.value)
}

export const getInvalidStep = (
  breadcrumb: IBreadCrumb[],
  step: number,
  isMediumOrLowRisk: boolean = false
) => {
  return breadcrumb.findIndex(
    (item, index) =>
      item.statut === '' &&
      !item.done &&
      index !== step &&
      isPatrimonyStepAvailable(isMediumOrLowRisk, index)
  )
}
export const getLastVisitedStep = (
  breadcrumb: IBreadCrumb[],
  isMediumOrLowRisk: boolean = false
) => {
  return breadcrumb.findIndex(
    (item, index) =>
      !item.done &&
      item.statut === undefined &&
      isPatrimonyStepAvailable(isMediumOrLowRisk, index)
  )
}

export const isPatrimonyStepAvailable = (
  isMediumOrLowRisk: boolean,
  step: number
): boolean => {
  return isMediumOrLowRisk ? step !== 2 : true
}

export const getUpdatedBreadcrumb = (
  breadcrumb: IBreadCrumb[],
  isMediumOrLowRisk: boolean
) => {
  const newBreadcrumb = breadcrumb

  newBreadcrumb[5].titre = 'Validation'

  return newBreadcrumb
}

export const isJourneySigned = (journeyStatus: string) => journeyStatus === '6'

export const createFileObject = (bits: BlobPart[], name: string): File => {
  // ie11 does not support File constructor
  try {
    return new File(bits, name)
  } catch (e) {
    const blob = new Blob(bits, { type: 'text/plain' })

    return Object.assign(blob, { lastModified: Date.now(), name })
  }
}

export const getPrefillPhone = (
  phones: {
    telephone_mobile_1_num: string
    telephone_mobile_2_num: string
    telephone_mobile_3_num: string
  },
  journeyStatus: string
) => {
  if (journeyStatus === '5') {
    return phones.telephone_mobile_1_num
  }

  const validPhone =
    [
      phones.telephone_mobile_1_num,
      phones.telephone_mobile_2_num,
      phones.telephone_mobile_3_num,
    ].find((phone) => validateStep4FranceNumber(phone)) || ''
  return updateStep4Phone(validPhone)
}

export const updateStep4Phone = (phone: string) => {
  const regex = RegExp(/^(00)[6-7][0-9 ]*$/)

  if (regex.test(phone)) {
    return phone.substring(1)
  }

  return phone
}

export const isFrenchColony = (country: string) => {
  return FRENCH_COLONIES.indexOf(country) !== -1
}

export const isExceptionCountry = (country: string) => {
  return EXCLUDED_FRENCH_COLONIES.indexOf(country) !== -1
}

export const computeAddressType = (
  country: string | undefined,
  value: string
): string => {
  // Cases covered where are:
  // - no value selected
  // - country and value are the same
  // - country and value are french colonies
  if (
    !value ||
    country === value ||
    (country && isFrenchColony(country) && isFrenchColony(value))
  ) {
    return DONT_CHANGE_DOMICILE_FISCALE
  }
  // Cases covered where are:
  // - country and value are different and value is not an exception country
  if (country && country !== value && !isExceptionCountry(value)) {
    return DOMICILE_FISCALE_DIFFERENT
  }
  // Cases covered where are:
  // - country was not read and value is not an exception country
  if (!country && !isExceptionCountry(value)) {
    return IS_YOUR_DOMICILE_FISCALE_DIFFERENT
  }
  // Cases covered where are:
  // - value is an exception country
  if (isExceptionCountry(value)) {
    return DONT_CHANGE_DOMICILE_FISCALE
  }
  // fallback
  return DONT_CHANGE_DOMICILE_FISCALE
}

export const isGeneralError = (error: string) => {
  return DEFAULT_ERROR_MESSAGE.some((message) => error.includes(message))
}

export function isServerError(error: Error) {
  const { message } = error
  const match = message.match(/status:\s*(\d+)/) // Regular expression to find "status: <code>"

  if (match) {
    const status = parseInt(match[1], 10)
    return status >= INTERNAL_SERVER_ERROR
  }
}

export const handleError = (
  error: Error,
  history: any,
  handleServerError: boolean = false
) => {
  if (isUnauthorized(error)) {
    history.push(ROUTES.unauthorized)
    return
  }
  if (isServerError(error) && handleServerError) {
    history.push(ROUTES.serverError)
  }
}

export const getUploadCount = () => {
  return +(localStorage.getItem('uploadCount') ?? '0')
}

export const setUploadCount = (limit: number = 0) => {
  localStorage.setItem('uploadCount', limit.toString())
}

export const processCitiesList = (communes: ICommuneData[]) =>
  communes.map((commune: ICommuneData) => ({
    value: commune.code_commune,
    label: `${commune.ladac6} (${commune.cadpo})`,
    postalCode: commune.cadpo,
  }))

export function asyncDebounce(func: Function, wait: number) {
  const debounced = debounce(async (resolve, reject, args) => {
    try {
      const result = await func(...args)
      resolve(result)
    } catch (error) {
      reject(error)
    }
  }, wait)

  function returnFunc(...args: any) {
    return new Promise((resolve, reject) => {
      debounced(resolve, reject, args)
    })
  }

  return returnFunc
}

export function isTrue(value: string | boolean) {
  return value === true || value === 'true'
}

export function convertNumericDateToString(numericDate: string) {
  if (!numericDate.length) {
    return ''
  }

  const anumericDate = numericDate.split(`/`)

  if (anumericDate.length !== 3) {
    return ''
  }
  const options = { year: 'numeric', month: 'long', day: 'numeric' }

  const event = new Date(
    Date.UTC(
      Number(anumericDate[2]),
      Number(anumericDate[1]) - 1,
      Number(anumericDate[0]),
      0,
      0,
      0
    )
  )

  return event
    .toLocaleDateString('fr-FR', options)
    .replace(/(^.|\s+.)/g, (m) => m.toUpperCase())
}

export function getYearImpots() {
  const date = new Date()
  return date.getMonth() + 1 > 10 ? date.getFullYear() : date.getFullYear() - 1
}

export const b64toBlob = (
  b64Data: string,
  contentType = 'application/pdf',
  sliceSize = 512
) => {
  const byteCharacters = atob(b64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i += 1) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }
  return new Blob(byteArrays, { type: contentType })
}

// Will return the status code from errors message like: "Error: The request responded with status: 403"
export const extractStatusCode = (error: Error): number | null => {
  const statusMatch = error.message.match(/status: (\d+)/)
  return statusMatch ? parseInt(statusMatch[1], 10) : null
}
